import { useEffect, useRef } from 'react';

import YouTube, { type YouTubeEvent } from 'react-youtube';

import { styled } from '@prose-ui/legacy';
import type { RequireAtLeastOne } from 'type-fest';

const Container = styled.div`
  height: 100%;
  width: 100%;
`;

type VideoEvents = {
  onPlay: () => void;
  onPause: () => void;
  onEnd: () => void;
};

type YoutubeVideoProps = {
  onClose: () => void;
  isOpen: boolean;
  videoId: string;
  isAutoplay?: boolean;
  events?: RequireAtLeastOne<VideoEvents>;
  dataTestId?: string;
};

const YoutubeVideo = ({
  isOpen,
  onClose,
  videoId,
  events,
  isAutoplay = false,
  dataTestId,
}: YoutubeVideoProps) => {
  /**
   * Using ref instead of event
   * Accessing the target to close it before it was opened throws an error
   * https://github.com/tjallingt/react-youtube/issues/211
   */
  const videoRef = useRef<any>(null);

  const wasOpened = useRef(false);

  useEffect(() => {
    if (isOpen) {
      wasOpened.current = true;
    }

    if (videoRef.current) {
      if (isOpen && isAutoplay) {
        videoRef.current.playVideo();
      }

      if (!isOpen && wasOpened.current) {
        videoRef?.current?.stopVideo();
      }
    }
  }, [isOpen, isAutoplay]);

  const onEnd = () => {
    if (events?.onEnd) {
      events.onEnd();
    }
    onClose();
  };

  return (
    <Container data-testid={dataTestId}>
      <YouTube
        onEnd={onEnd}
        onPause={events?.onPause}
        onPlay={events?.onPlay}
        onReady={(event: YouTubeEvent) => {
          videoRef.current = event.target;
        }}
        opts={{
          height: '100%',
          width: '100%',
          playerVars: { autoplay: 0, rel: 0, playsinline: 1 },
        }}
        style={{ height: '100%', width: '100%', lineHeight: 0 }}
        title="YouTube video player"
        videoId={videoId}
      />
    </Container>
  );
};

export default YoutubeVideo;
